<template>
  <div
    class="us-input-select-wrapper"
    :class="{ 'us-input-select--disabled': disabled }"
  >
    <div
      v-if="$slots['heading'] || heading"
      class="display--d2--semibold input-heading-wrapper"
    >
      <component :is="`h${headingLevel}`" v-if="heading">
        {{ heading }}
      </component>
      <slot name="heading" />
    </div>
    <div
      v-if="$slots['description'] || description"
      class="input-description-wrapper font--p1"
    >
      <p v-if="description">
        {{ description }}
      </p>
      <slot name="description" />
    </div>

    <div
      ref="select-wrapper"
      data-test="us-input-select"
      :class="[
        'us-input-select',
        { 'us-input--error': error },
        { 'us-input-select--multiselect': multiselect }
      ]"
    >
      <div ref="select__selected" class="select__selected">
        {{ selectText }}
      </div>
      <div class="select__options-container">
        <label
          v-for="option in options"
          :key="option.value"
          class="select__option"
          :data-select-value="option.value"
        >
          <input type="checkbox" :name="option.label" @input="onInput" />
          <span data-select-label>{{ option.label }}</span>
        </label>
      </div>
      <USIcon
        :diameter="selectDiameter"
        class="select__open-icon"
        :name="closedArrow"
      />
      <USIcon
        :diameter="selectDiameter"
        class="select__close-icon"
        :name="openArrow"
      />
    </div>
    <div v-if="error" class="error">
      {{ error }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import { Select, option } from '@base/@motiv/fe-patterns'

import USIcon from '../USIcon/USIcon.vue'

export default defineComponent({
  name: 'USInputSelect',
  components: {
    USIcon
  },
  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      default: [] as string[]
    },
    options: {
      type: Array as PropType<option[]>,
      required: true
    },
    multiselect: {
      type: Boolean,
      default: false
    },
    maxDropdownHeight: {
      type: Number,
      default: 500
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selectText: {
      type: String,
      default: 'Select'
    },
    error: {
      type: String,
      default: ''
    },
    heading: {
      type: String,
      default: ''
    },
    headingLevel: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
      default: 1
    },
    description: {
      type: String,
      default: ''
    },
    closedArrow: {
      type: String,
      default: 'chevron-up'
    },
    openArrow: {
      type: String,
      default: 'chevron-down'
    },
    selectDiameter: {
      type: Number,
      default: 18
    },
    direction: {
      type: String as PropType<'any' | 'bottom' | 'top'>,
      default: 'any'
    }
  },
  emits: ['update:modelValue'],
  data: () => ({
    selectInstance: null as Select | null
  }),
  watch: {
    modelValue(val) {
      if (this.selectInstance) {
        const valClone = [...val]
        const currentVals = this.selectInstance.getValues()
        const equal =
          valClone.sort().toString() == currentVals.sort().toString()

        if (!equal) {
          this.selectInstance?.setValuesManually(valClone)
        }
      }
    }
  },
  mounted() {
    const select = this.$refs['select-wrapper'] as HTMLElement

    this.selectInstance = new Select(
      select,
      '.select__selected',
      '.select__options-container',
      '.select__option',
      this.multiselect,
      this.options,
      this.selectText,
      this.maxDropdownHeight,
      this.direction
    )

    this.selectInstance.setValuesManually([...this.modelValue])

    this.selectInstance.on('update', () => {
      this.$emit('update:modelValue', this.selectInstance?.getValues())
    })

    this.selectInstance.init()
  },
  unMounted() {
    if (this.selectInstance) this.selectInstance.destroy()
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

@use '@base/@motiv/fe-patterns/styles/select' as *;

@use '../../styles/_scss/drop-shadow' as ds;
@use '@base/@motiv/fe-patterns/styles/checkbox' as *;

@include select(
  $select-selector: '.us-input-select',
  $selected-selector: '.select__selected',
  $options-container-selector: '.select__options-container',
  $option-selector: '.select__option',
  $close-icon-selector: '.select__close-icon',
  $open-icon-selector: '.select__open-icon',
  $icon-size: 18px,
  $icon-right-offset: 22px,
  $transition-time: var(--animation-speed-medium)
);

.us-input-select {
  background-color: var(--color--neutral-a5);
  border: 1px solid var(--color--primary-b3);
  border-radius: 11px;
  position: relative;
  font-size: var(--font-size-p1);
  user-select: none;
  transition: all var(--animation-speed-medium) ease;

  &.active {
    border-bottom-right-radius: 0px;

    .select__option {
      pointer-events: all;
    }
  }

  &.us-input--error {
    border-color: var(--color--semantic--error-2) !important;
  }

  &:disabled {
    background-color: var(--color--neutral-a4);
    border-color: transparent !important;

    &:hover,
    &:focus {
      cursor: not-allowed;
    }
  }

  &:hover,
  &:focus,
  &.active {
    border-color: var(--color--primary-b1);
  }
}

.select__selected {
  overflow: hidden;
  padding: 14px 27px;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: breakpoint.$breakpoint--7) {
    padding: 20px 40px;
  }
}

.us-icon {
  pointer-events: none;
}

.us-icon :deep(svg) {
  color: var(--color--primary-b1);
  height: 100%;
  pointer-events: none;
}

.select__options-container {
  @include ds.drop-shadow-100;

  border-radius: 11px 0px 11px 11px;
  margin-top: 10px;
  overflow: hidden;
}

.select__option {
  background-color: var(--color--neutral-a5);
  cursor: pointer;
  padding: 14px 27px;
  pointer-events: none;

  &.option--selected {
    background-color: var(--color--secondary-c2);
  }

  &:hover,
  &:focus,
  &:focus-within {
    color: var(--color--neutral-a5);
    background-color: var(--color--primary-b1);
  }

  @media (min-width: breakpoint.$breakpoint--7) {
    padding: 22px 40px;
  }
}

.us-input-select--disabled {
  .us-input-select {
    background-color: var(--color--neutral-a4);
    border-color: transparent !important;
    cursor: not-allowed;
    color: var(--color--primary-b1);
    pointer-events: none;

    .us-icon :deep(svg) {
      color: var(--color--primary-b1);
    }
  }
}

.us-input-select--multiselect {
  @include checkbox(
    $checkbox-selector: '.select__option',
    $gutter-between-checkbox-label: 10px,
    $transition-speed: var(--animation-speed-medium),
    $background-color: var(--color--secondary-c2),
    $hover-background-color: var(--color--secondary-c2),
    $breakpoint: var(--breakpoint--7),
    $checkbox-size-sm: 19px,
    $checkbox-size-lg: 24px,
    $checked-background-color: var(--color--secondary-c1),
    $checkmark-size-sm: 10px,
    $checkmark-size-lg: 14px,
    $checkmark-color: #ffffff,
    $focus-color: transparent
  );

  input[type='checkbox'] {
    border-radius: 4px;
    border: 2px solid transparent;
    min-height: 19px;
    min-width: 19px;

    @media (min-width: breakpoint.$breakpoint--9) {
      min-height: 24px;
      min-width: 24px;
    }

    &:disabled {
      background-color: var(--color--neutral-a4);
    }

    &:hover {
      border: 2px solid var(--color--primary-b1);
    }
  }

  .select__option {
    &:hover,
    &:focus,
    &:focus-within {
      color: var(--color--neutral-a1);
      background-color: white;
    }
  }
}

.input-heading-wrapper {
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing--8);
}

.input-description-wrapper {
  margin-bottom: var(--spacing--7);
}

.error {
  color: var(--color--semantic--error-2);
  font-size: var(--font-size-d3);
  padding-left: 27px;
  padding-top: var(--spacing--8);

  @media (min-width: breakpoint.$breakpoint--7) {
    padding-left: 40px;
  }
}
:deep(.us-input-select--multiselect .select__option input:checked:after) {
  left: 50%;
  transform: translateY(calc(-50% - 0px)) rotate(45deg)
    translateX(calc(-50% + 0px));
}
</style>
